<template>
    <div class="employee-contact-editor">
        <div class="employee-contact-editor__head">
            <div>
                <strong>{{ title }}</strong>
                <small v-if="hint">{{ hint }}</small>
            </div>
            <b-button
                size="sm"
                variant="outline-primary"
                :disabled="disabled"
                @click="addContact"
            >
                <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                Добавить контакт
            </b-button>
        </div>

        <div
            v-for="(contact, index) in rows"
            :key="contact.local_id || contact.id || index"
            class="employee-contact-editor__row"
        >
            <b-form-select
                :value="contact.contact_type"
                :options="contactTypeOptions"
                :disabled="disabled"
                aria-label="Тип контакта"
                @input="updateContact(index, {contact_type: $event}, true)"
            ></b-form-select>
            <b-form-input
                :value="contact.label"
                maxlength="255"
                placeholder="Подпись (необязательно)"
                :disabled="disabled"
                aria-label="Подпись контакта"
                @input="updateContact(index, {label: $event}, true)"
            ></b-form-input>
            <phone-input
                v-if="contact.contact_type === 'phone'"
                :value="contact.value"
                mode="contact"
                :show-feedback="false"
                :disabled="disabled"
                placeholder="+375… или добавочный"
                @input="updateContact(index, {value: $event}, true)"
            ></phone-input>
            <b-form-input
                v-else
                :value="contact.value"
                :type="contact.contact_type === 'email' ? 'email' : 'text'"
                :inputmode="contact.contact_type === 'email' ? 'email' : 'text'"
                :placeholder="contact.contact_type === 'email'
                    ? 'email@example.com'
                    : '@username или t.me/username'"
                :disabled="disabled"
                :aria-label="contact.contact_type === 'email'
                    ? 'Email'
                    : 'Telegram username'"
                @input="updateContact(index, {value: $event}, true)"
                @blur="normalizeContact(index)"
            ></b-form-input>
            <div class="employee-contact-editor__meta">
                <b-badge :variant="sourceVariant(contact.source)">
                    {{ sourceLabel(contact.source) }}
                </b-badge>
                <b-button
                    size="sm"
                    variant="outline-danger"
                    title="Удалить контакт"
                    aria-label="Удалить контакт"
                    :disabled="disabled"
                    @click="removeContact(index)"
                >
                    <i class="fas fa-times" aria-hidden="true"></i>
                </b-button>
            </div>
        </div>

        <div v-if="!rows.length" class="employee-contact-editor__empty">
            Дополнительных контактов пока нет.
        </div>
        <b-alert v-if="currentValidationError" show variant="danger" class="mb-0 mt-2">
            {{ currentValidationError }}
        </b-alert>
    </div>
</template>

<script>
import PhoneInput from '@/components/PhoneInput.vue';
import {
    employeeContactsValidationError,
    prepareEmployeeContact,
    prepareEmployeeContacts
} from '@/utils/employeeContacts';

export default {
    name: 'EmployeeContactEditor',
    components: {PhoneInput},
    model: {
        prop: 'value',
        event: 'input'
    },
    props: {
        value: {
            type: Array,
            default: () => []
        },
        primaryContacts: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Контактные данные'
        },
        hint: {
            type: String,
            default: 'Телефон, email или Telegram username. Эти контакты не используются для авторизации.'
        }
    },
    data() {
        return {
            nextLocalId: 1,
            contactTypeOptions: [
                {value: 'phone', text: 'Телефон'},
                {value: 'email', text: 'Email'},
                {value: 'telegram', text: 'Telegram username'}
            ]
        };
    },
    computed: {
        rows() {
            return Array.isArray(this.value) ? this.value : [];
        },
        currentValidationError() {
            return employeeContactsValidationError(this.rows, {
                primaryContacts: this.primaryContacts
            });
        }
    },
    watch: {
        currentValidationError: {
            immediate: true,
            handler(value) {
                this.$emit('validation', value);
            }
        }
    },
    methods: {
        emitRows(rows) {
            this.$emit('input', rows);
        },
        addContact() {
            this.emitRows(this.rows.concat([{
                local_id: `contact-${Date.now()}-${this.nextLocalId++}`,
                contact_type: 'phone',
                label: '',
                value: '',
                source: 'manual'
            }]));
        },
        updateContact(index, patch, markManual) {
            const rows = this.rows.map(function (contact, rowIndex) {
                if (rowIndex !== index) return contact;
                return Object.assign({}, contact, patch, markManual
                    ? {source: 'manual'}
                    : {});
            });
            this.emitRows(rows);
        },
        normalizeContact(index) {
            try {
                const prepared = prepareEmployeeContact(this.rows[index], index);
                if (!prepared) return;
                const valueChanged = prepared.value !== String(
                    this.rows[index] && this.rows[index].value || ''
                ).trim();
                this.updateContact(index, {
                    value: prepared.value,
                    label: prepared.label || ''
                }, valueChanged);
            } catch (error) {
                // Ошибка уже показана общим валидатором редактора.
            }
        },
        removeContact(index) {
            this.emitRows(this.rows.filter(function (contact, rowIndex) {
                return rowIndex !== index;
            }));
        },
        preparedContacts() {
            return prepareEmployeeContacts(this.rows, {
                primaryContacts: this.primaryContacts
            });
        },
        validationError() {
            return this.currentValidationError;
        },
        sourceLabel(source) {
            const labels = {
                manual: 'Вручную',
                google_contacts: 'Google',
                google_sheets: 'Google',
                google_workforce: 'Google',
                '1c_zup': '1С',
                user_account: 'Учётная запись',
                argus: 'Учётная запись'
            };
            return labels[source] || source || 'Вручную';
        },
        sourceVariant(source) {
            if (source === 'manual') return 'secondary';
            if (source === '1c_zup') return 'info';
            if (source === 'user_account' || source === 'argus') return 'primary';
            return 'success';
        }
    }
};
</script>

<style scoped>
.employee-contact-editor {
    min-width: 0;
}

.employee-contact-editor__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.employee-contact-editor__head strong,
.employee-contact-editor__head small {
    display: block;
}

.employee-contact-editor__head small {
    margin-top: 3px;
    color: #6c757d;
}

.employee-contact-editor__row {
    display: grid;
    grid-template-columns: minmax(180px, 0.75fr) minmax(140px, 0.75fr) minmax(220px, 1.35fr) auto;
    gap: 8px;
    align-items: start;
    margin-bottom: 8px;
}

.employee-contact-editor__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 38px;
}

.employee-contact-editor__empty {
    padding: 10px 12px;
    border: 1px dashed #ced6de;
    border-radius: 6px;
    color: #6c757d;
    font-size: 0.86rem;
}

@media (max-width: 767.98px) {
    .employee-contact-editor__head,
    .employee-contact-editor__row {
        display: grid;
        grid-template-columns: 1fr;
    }

    .employee-contact-editor__head .btn {
        width: 100%;
    }

    .employee-contact-editor__meta {
        justify-content: space-between;
    }
}
</style>
